import React from 'react';
import {useFormikContext, useField, FormikContextType} from 'formik';
import styles from './CustomInput.module.css';

export type InputSize = 'sm' | 'md' | 'lg';

type CommonProps = {
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  type?: React.HTMLInputTypeAttribute;
    autoComplete?: string;
  disabled?: boolean;
  readOnly?: boolean;
  size?: InputSize; // visual size, aligns with CustomSelect: sm=32, md=40, lg=48
  variant?: 'outline' | 'bare';
  icon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  multiline?: boolean;
  rows?: number;
  showError?: boolean;
  error?: string; // force error text
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
};

type FormikModeProps = {
  name: string; // when provided, tries to use Formik context
  value?: never;
  defaultValue?: never;
  onChange?: never;
};

type NonFormikModeProps = {
  name?: never;
  value?: string | number | undefined;
  defaultValue?: string | number | undefined;
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
};

export type CustomInputProps = CommonProps & (FormikModeProps | NonFormikModeProps);

function useMaybeFormik(name?: string): FormikContextType<any> | null {
  try {
    // useFormikContext throws outside of context; catch to support non-Formik mode
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const ctx = useFormikContext<any>();
    if (name) return ctx;
    return null;
  } catch {
    return null;
  }
}

export const CustomInput: React.FC<CustomInputProps> = (props) => {
  const {
    className,
    inputClassName,
    placeholder,
    type = 'text',
    autoComplete,
    disabled,
    readOnly,
    size = 'lg',
    variant = 'outline',
    icon,
    rightIcon,
    multiline,
    rows = 3,
    showError = true,
    error: errorProp,
    onFocus,
    onBlur,
    onKeyDown,
    ...rest
  } = props as CustomInputProps & Record<string, any>;

  const formik = useMaybeFormik((rest as any).name);
  const isFormik = Boolean(formik && (rest as any).name);

  const sizeClass = size === 'sm' ? 'ci--sm' : size === 'md' ? 'ci--md' : 'ci--lg';
  const variantClass = variant === 'bare' ? 'ci--bare' : 'ci--outline';

  let fieldProps: any = {};
  let errorText: string | undefined = errorProp;

  if (isFormik) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [field] = useField((rest as any).name as string);
    fieldProps = field;
    const {status, errors, touched} = formik!;
    const n = (rest as any).name as string;
    if (errorText == null) {
      if (status && status[n]) errorText = status[n];
      else if (touched && (touched as any)[n] && errors && (errors as any)[n]) errorText = (errors as any)[n];
    }
  } else {
    fieldProps = {
      value: (rest as any).value,
      defaultValue: (rest as any).defaultValue,
      onChange: (rest as any).onChange,
    };
  }

  const inputElement = multiline ? (
    <textarea
      {...fieldProps}
      placeholder={placeholder}
      autoComplete={autoComplete}
      disabled={disabled}
      readOnly={readOnly}
      rows={rows}
      className={`${styles['ci__input']} ${inputClassName || ''}`.trim()}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    />
  ) : (
    <input
      {...fieldProps}
      type={type}
      placeholder={placeholder}
      autoComplete={autoComplete}
      disabled={disabled}
      readOnly={readOnly}
      className={`${styles['ci__input']} ${inputClassName || ''}`.trim()}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    />
  );

  return (
    <div className={`${styles['ci-wrap']} ${className || ''}`.trim()}>
      <div className={`${styles.ci} ${styles[sizeClass]} ${styles[variantClass]}`.trim()}>
        {icon ? <div className={styles['ci__icon-left']}>{icon}</div> : null}
        {inputElement}
        {rightIcon ? <div className={styles['ci__icon-right']}>{rightIcon}</div> : null}
      </div>
      {showError && errorText ? <div className={styles['ci__error']}>{errorText}</div> : null}
    </div>
  );
};

export default CustomInput;


